import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import {
  selectIsBookConversion,
  selectVacancyReason,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { FormSelectableWithValue, HomeUsage } from "@covertree/types";
import { FormRadioGroup } from "@covertree/ui";

import { Box } from "@mui/material";

import { EffectiveDateExposureAndLapseForm } from "../validatorsAndTypes/effectiveDateExposureAndLapseForm";
import { FormVacancyReasonSelect } from "./VacancyReasonSelect";

enum TimeSpentAtHome {
  MainResidence = "Main residence",
  SeasonalResidence = "Seasonal residence",
  Vacant = "This home is vacant",
}

export const getItems = (
  showVacancyReasonSelect: boolean,
  disabled: boolean,
  isD2C: boolean
): FormSelectableWithValue<HomeUsage>[] => [
  {
    title: TimeSpentAtHome.MainResidence,
    value: HomeUsage.MainResidence,
    image: "/images/card-icons/home.webp",
    imageAlt: "house",
    subtitle: "More than 6 months per year",
    disabled,
  },
  {
    title: TimeSpentAtHome.SeasonalResidence,
    value: HomeUsage.SeasonalResidence,
    image: "/images/card-icons/sun.webp",
    imageAlt: "sun",
    subtitle: "Less than 6 months per year",
    disabled,
  },
  {
    title: TimeSpentAtHome.Vacant,
    value: HomeUsage.HomeIsVacant,
    image: "/images/card-icons/empty-home.webp",
    imageAlt: "narrow house",
    disabled: showVacancyReasonSelect ? false : disabled,
    additionalBodyContentDirection: "column",
    additionalBodyContent:
      !isD2C && showVacancyReasonSelect ? (
        <Box>
          <FormVacancyReasonSelect
            label="Why is applicant's home vacant?"
            name="vacancyReason"
          />
        </Box>
      ) : undefined,
  },
];

interface TimeSpentAtHomeRadioGroupProps {
  isD2C: boolean;
  onOptionSelect?: (option: unknown) => void;
}

export const TimeSpentAtHomeRadioGroup = ({
  isD2C,
  onOptionSelect,
}: TimeSpentAtHomeRadioGroupProps) => {
  const { control, watch } =
    useFormContext<EffectiveDateExposureAndLapseForm>();

  const vacancyReason = useNewBusinessSelector(selectVacancyReason);
  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);

  const residencyTypeValue = watch("homeUsage");

  const items = useMemo(() => {
    return getItems(
      residencyTypeValue === HomeUsage.HomeIsVacant || !!vacancyReason,
      isBookConversion,
      isD2C
    );
  }, [vacancyReason, residencyTypeValue]);

  return (
    <FormRadioGroup
      name="homeUsage"
      control={control}
      selected={residencyTypeValue}
      direction="column"
      label={isD2C ? "" : "How much time does applicant spend at his home?"}
      items={items}
      onOptionSelect={onOptionSelect}
    />
  );
};
